(function (window, document) {
  if (typeof document === "undefined" || typeof window === "undefined") {
    return;
  }

  const Config = (window && window.MVTConfigModule && window.MVTConfigModule.getConfig()) || {};
  const PROJECT_KEY_PARAM = Config.projectKeyParam;
  const ASSIGNMENT_COOKIE_NAME_PREFIX = Config.assignmentCookieNamePrefix;
  const ASSIGNMENT_COOKIE_DAYS = Config.assignmentCookieDays;
  const CONFIG_API_ENDPOINT = Config.configApiEndpoint;
  const CHANGES_API_BASE = Config.changesApiBase;
  const FLICKER_CLASS = Config.flickerClass;
  const VISUAL_EDITOR_URL = Config.visualEditorUrl;
  const PUBLIC_API_BASE = Config.publicApiBase;

  window.__MVT_CONSENT_REQUIRED__ = Config.cookieConsentEnabled === true;
  window.__MVT_CONSENT_GRANTED__ = window.__MVT_CONSENT_GRANTED__ === true;

  let lastProcessedUrl = null;

  let isProcessing = false;

  let navigationListenersInitialized = false;

  let engineInitialized = false;

  function normalizeUrlForComparison(url) {
    if (!url) return "";

    const trimmed = url.trim().toLowerCase();
    const withoutProtocol = trimmed.replace(/^https?:\/\//i, "");

    const withoutDefaultDoc = withoutProtocol.replace(
      /\/(?:index|default)\.(?:html?|php|aspx?)$/i,
      "/",
    );
    const withoutTrailing = withoutDefaultDoc.replace(/\/+$/, "");

    const withoutHash = withoutTrailing.replace(/#.*$/, "");

    const withoutLinker = withoutHash.replace(/([?&])_mvt_lt=[^&]*&?/i, "$1").replace(/[?&]$/, "");
    return withoutLinker;
  }

  function initializeEngine(isNavigationTrigger = false) {
    if (!isNavigationTrigger && engineInitialized) {
      console.warn("MVT Engine: Already initialized, skipping duplicate initialization");
      return;
    }

    const Utils = window.MVTUtils;
    const Assignment = window.MVTAssignmentModule;
    const Fetch = window.MVTFetchModule;
    const Preview = window.MVTPreviewModule;

    const projectKey = Config.projectKey || Utils.getProjectKey(PROJECT_KEY_PARAM);

    if (projectKey && Utils.extractIncomingLinkToken) {
      window.__MVT_LINK_TOKEN = Utils.extractIncomingLinkToken(projectKey);
    }

    if (!projectKey) {
      console.error("MVT Engine: Initialization failed. Project key is missing.");
      Utils.removeFlickerClass(FLICKER_CLASS);
      return;
    }

    if (window.MVTClaims && window.MVTClaims.armProviderClaimListener) {
      window.MVTClaims.armProviderClaimListener({
        publicApiBase: PUBLIC_API_BASE,
        projectKey: projectKey,
      });
    }

    if (!isNavigationTrigger) {
      engineInitialized = true;
    }

    const currentUrl = window.location.href;
    const normalizedCurrentUrl = normalizeUrlForComparison(currentUrl);

    if (isNavigationTrigger) {
      if (isProcessing) {
        return;
      }
      if (lastProcessedUrl === normalizedCurrentUrl) {
        return;
      }

      const Application = window.MVTApplicationModule;
      if (Application && typeof Application.cleanupAppliedVariants === "function") {
        try {
          Application.cleanupAppliedVariants();
        } catch (cleanupError) {
          console.warn("MVT Engine: Error during variant cleanup on navigation", cleanupError);
        }
      }
    }

    isProcessing = true;
    lastProcessedUrl = normalizedCurrentUrl;

    const isVerifyMode = (() => {
      try {
        const pageUrl = new URL(window.location.href);
        const verifyParam = pageUrl.searchParams.get("verify");
        return verifyParam === projectKey || verifyParam === "true" || verifyParam === "1";
      } catch (e) {
        return false;
      }
    })();

    const isInEditorMode = Utils.isInEditorMode();
    const isOpenedInNewTab = window.opener;

    if (isOpenedInNewTab && isInEditorMode && !isVerifyMode) {
      console.log("Editor mode detected");

      var supportsEventListener = "addEventListener" in window;
      var eventMethod = supportsEventListener ? "addEventListener" : "attachEvent";
      var messageEvent = supportsEventListener ? "message" : "onmessage";

      var editorOrigin = (function () {
        try {
          return new URL(VISUAL_EDITOR_URL, window.location.href).origin;
        } catch (e) {
          return null;
        }
      })();

      if (!editorOrigin) {
        console.error(
          "[MVT] Could not derive the visual-editor origin; refusing to open the editor channel.",
        );
        Utils.removeFlickerClass(FLICKER_CLASS);
        return;
      }

      window[eventMethod](
        messageEvent,
        (event) => {
          if (event.origin !== editorOrigin) return;
          if (event.source !== window.opener) return;

          var data = event.data || {};

          if ("LOAD_EDITOR" === data.event_id) {
            console.log("Loading live editor script on message");
            Utils.executeWhenReady(Utils.loadScript, VISUAL_EDITOR_URL);
          }
        },
        false,
      );

      if (window.opener) {
        try {
          window.opener.postMessage(
            {
              event_id: "VISUAL_EDITOR",
              data: {
                status: "load-editor",
              },
            },
            editorOrigin,
          );
        } catch (error) {
          console.error(error);
        }
      }

      Utils.removeFlickerClass(FLICKER_CLASS);
      return;
    }

    if (isInEditorMode && !isVerifyMode) {
      Utils.removeFlickerClass(FLICKER_CLASS);
      return;
    }

    const isLivePreviewMode = Utils.isLivePreviewMode();
    if (isLivePreviewMode) {
      console.log("Live preview mode detected");
      Preview.fetchChangesAndApply(PUBLIC_API_BASE);
      Utils.removeFlickerClass(FLICKER_CLASS);
      return;
    }

    const assignedGroup = Assignment.assignUser(
      projectKey,
      ASSIGNMENT_COOKIE_NAME_PREFIX,
      ASSIGNMENT_COOKIE_DAYS,
    );

    const linkLanding = !!window.__MVT_LINK_TOKEN;
    if (
      !linkLanding &&
      window.MVTSessionTrack &&
      typeof window.MVTSessionTrack.start === "function"
    ) {
      try {
        window.MVTSessionTrack.start({
          projectKey: projectKey,
          publicApiBase: PUBLIC_API_BASE,
        });
      } catch (e) {}
    }

    if (
      !isInEditorMode &&
      !isLivePreviewMode &&
      window.MVTIdentifyCapture &&
      typeof window.MVTIdentifyCapture.start === "function"
    ) {
      try {
        window.MVTIdentifyCapture.start({
          projectKey: projectKey,
          publicApiBase: PUBLIC_API_BASE,
        });
      } catch (e) {}
    }

    if (!isInEditorMode && !isLivePreviewMode) {
      Fetch.fetchConfigAndRun(
        projectKey,
        assignedGroup,
        CONFIG_API_ENDPOINT,
        FLICKER_CLASS,
        CHANGES_API_BASE,
        PUBLIC_API_BASE,
      ).finally(() => {
        isProcessing = false;

        if (
          linkLanding &&
          window.MVTSessionTrack &&
          typeof window.MVTSessionTrack.start === "function"
        ) {
          try {
            window.MVTSessionTrack.start({
              projectKey: projectKey,
              publicApiBase: PUBLIC_API_BASE,
            });
          } catch (e) {}
        }
      });
    }
  }

  function setupNavigationListeners() {
    if (navigationListenersInitialized) {
      return;
    }
    navigationListenersInitialized = true;

    let navigationTimeout = null;
    const handleNavigation = () => {
      if (navigationTimeout) {
        clearTimeout(navigationTimeout);
      }
      navigationTimeout = setTimeout(() => {
        const currentUrl = window.location.href;
        const normalizedCurrentUrl = normalizeUrlForComparison(currentUrl);

        if (lastProcessedUrl === normalizedCurrentUrl) {
          return;
        }

        initializeEngine(true);

        if (window.MVTRecheckMetrics) {
          setTimeout(() => {
            window.MVTRecheckMetrics();
          }, 100);
        }
      }, 100);
    };

    window.addEventListener("popstate", handleNavigation);

    if (!window._MVT_HISTORY_WRAPPED) {
      window._MVT_HISTORY_WRAPPED = true;
      const originalPushState = history.pushState.bind(history);
      const originalReplaceState = history.replaceState.bind(history);

      history.pushState = function (...args) {
        originalPushState.apply(this, args);
        handleNavigation();
      };

      history.replaceState = function (...args) {
        originalReplaceState.apply(this, args);
        handleNavigation();
      };
    }

    window.addEventListener("hashchange", handleNavigation);
  }

  if (engineInitialized) {
    console.warn("MVT Engine: Script already initialized, skipping duplicate execution");
  } else {
    initializeEngine();
    setupNavigationListeners();
  }

  window.MVT = window.MVT || {};
  window.MVT.consentGranted = function () {
    window.__MVT_CONSENT_GRANTED__ = true;
    if (
      window.MVTAssignmentModule &&
      typeof window.MVTAssignmentModule.flushPendingAssignment === "function"
    ) {
      window.MVTAssignmentModule.flushPendingAssignment();
    }
    if (window.MVTUtils && typeof window.MVTUtils.onConsentGranted === "function") {
      window.MVTUtils.onConsentGranted();
    }

    if (typeof window._MVT_PENDING_FORM_STAMP === "function") {
      const restampForms = window._MVT_PENDING_FORM_STAMP;
      window._MVT_PENDING_FORM_STAMP = null;
      try {
        restampForms();
      } catch (error) {
        console.error(error);
      }
    }
    if (window.MVTTrack && typeof window.MVTTrack.flushPendingTracking === "function") {
      window.MVTTrack.flushPendingTracking();
    }
  };

  window.MVT.consentRevoked = function () {
    window.__MVT_CONSENT_GRANTED__ = false;
    window.__MVT_PENDING_TRACK__ = [];
  };

  window.MVT.refresh = function () {
    lastProcessedUrl = null;
    initializeEngine(true);
    if (window.MVTRecheckMetrics) {
      setTimeout(function () {
        window.MVTRecheckMetrics();
      }, 100);
    }
  };

  window.MVT.refreshElement = window.MVT.refresh;
})(window, document);
